/**
 * Modal para iniciar/ver progreso de clase progresiva
 */

import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button, Input, Select, Badge } from "@/components/ui";
import {
  X,
  Loader2,
  GraduationCap,
  Search,
  User,
  CheckCircle,
  Circle,
  Send,
  Clock,
  XCircle,
  Award,
  Settings,
  AlertTriangle,
} from "lucide-react";
import {
  useCreateClassProgress,
  useUpdateRequirement,
  useCompleteClass,
  useInvestClass,
  useSubmitForApproval,
} from "@/hooks/useProgress";
import { useClubClassConfig } from "@/hooks/useClubClassConfig";
import { useMembersByClub } from "@/hooks/usePersons";
import { useProgressClasses } from "@/hooks/useCatalogs";
import { useClub } from "@/hooks/useOrganization";
import { getActiveSections } from "@/lib/utils";
import { Timestamp } from "firebase/firestore";
import type {
  ClassProgress,
  MemberWithPerson as Member,
  ProgressClass,
} from "@/types";
import ClassRequirementConfigModal from "./ClassRequirementConfigModal";

const classProgressSchema = z.object({
  personId: z.string().min(1, "Selecciona una persona"),
  memberId: z.string().min(1, "Selecciona un miembro"),
  classId: z.string().min(1, "Selecciona una clase"),
});

type ClassProgressForm = z.infer<typeof classProgressSchema>;

interface ClassProgressModalProps {
  isOpen: boolean;
  onClose: () => void;
  clubId: string;
  periodId: string;
  classProgress?: ClassProgress | null;
}

const statusLabels: Record<string, string> = {
  in_progress: "En Progreso",
  completed: "Completada",
  pending_approval: "Pendiente de Aprobación",
  approved: "Aprobada",
  rejected: "Rechazada",
  invested: "Investido",
};

const statusColors: Record<string, string> = {
  in_progress: "bg-blue-100 text-blue-800",
  completed: "bg-green-100 text-green-800",
  pending_approval: "bg-orange-100 text-orange-800",
  approved: "bg-emerald-100 text-emerald-800",
  rejected: "bg-red-100 text-red-800",
  invested: "bg-yellow-100 text-yellow-800",
};

// Mapeo de nombres de clase a imagen de pin
const classImageMap: Record<string, string> = {
  amigo: "/images/class/amigo.jpg",
  companero: "/images/class/companero.jpg",
  compañero: "/images/class/companero.jpg",
  explorador: "/images/class/explorador.jpg",
  orientador: "/images/class/orientador.jpg",
  viajero: "/images/class/viajero.jpg",
  guia: "/images/class/guia.jpg",
  guía: "/images/class/guia.jpg",
};

function getClassImage(className: string): string {
  const normalized = className
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
  return (
    classImageMap[normalized] ||
    classImageMap[className.toLowerCase()] ||
    "/images/class/amigo.jpg"
  );
}

export default function ClassProgressModal({
  isOpen,
  onClose,
  clubId,
  periodId,
  classProgress,
}: ClassProgressModalProps) {
  const isViewing = !!classProgress;

  const createMutation = useCreateClassProgress();
  const updateRequirementMutation = useUpdateRequirement();
  const completeClassMutation = useCompleteClass();
  const investClassMutation = useInvestClass();
  const submitForApprovalMutation = useSubmitForApproval();

  const { data: members, isLoading: loadingMembers } = useMembersByClub(clubId);
  const { data: progressClasses, isLoading: loadingClasses } =
    useProgressClasses();
  const { data: club } = useClub(clubId);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<ClassProgressForm>({
    resolver: zodResolver(classProgressSchema),
  });

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedMember, setSelectedMember] = useState<Member | null>(null);
  const [selectedClass, setSelectedClass] = useState<ProgressClass | null>(
    null,
  );
  const [showConfigModal, setShowConfigModal] = useState(false);

  // Obtener configuración de requisitos del club para la clase seleccionada
  const watchClassId = watch("classId");
  const { data: clubClassConfig, isLoading: loadingConfig } =
    useClubClassConfig(clubId, watchClassId || "");

  // Filtrar miembros activos
  const activeMembers = members?.filter((m) => m.status === "active") || [];

  // Filtrar miembros por búsqueda
  const filteredMembers = activeMembers.filter(
    (m) =>
      searchTerm === "" ||
      m.person.fullName?.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  // Filtrar clases por tipo de club
  const filteredClasses =
    progressClasses?.filter(
      (c) =>
        c.status === "active" &&
        (!club || getActiveSections(club).includes(c.clubType)),
    ) || [];

  // Cuando cambia la clase seleccionada
  useEffect(() => {
    if (watchClassId && progressClasses) {
      const cls = progressClasses.find((c) => c.id === watchClassId);
      setSelectedClass(cls || null);
    }
  }, [watchClassId, progressClasses]);

  const handleSelectMember = (member: Member) => {
    setSelectedMember(member);
    setValue("personId", member.personId || member.id);
    setValue("memberId", member.id);
    setSearchTerm("");
  };

  const handleToggleRequirement = async (
    requirementId: string,
    currentState: boolean,
  ) => {
    if (!classProgress) return;

    await updateRequirementMutation.mutateAsync({
      progressId: classProgress.id,
      requirementId,
      completed: !currentState,
    });
  };

  const handleCompleteClass = async () => {
    if (!classProgress) return;
    await completeClassMutation.mutateAsync(classProgress.id);
  };

  const handleInvestClass = async () => {
    if (!classProgress) return;
    await investClassMutation.mutateAsync(classProgress.id);
  };

  const handleSubmitForApproval = async () => {
    if (!classProgress) return;
    await submitForApprovalMutation.mutateAsync(classProgress.id);
  };

  const onSubmit = async (data: ClassProgressForm) => {
    if (!selectedMember || !selectedClass) return;

    // Usar requisitos del club si están configurados, sino requisitos vacíos
    let requirements: {
      requirementId: string;
      name: string;
      completed: boolean;
    }[] = [];

    if (
      clubClassConfig?.requirements &&
      clubClassConfig.requirements.length > 0
    ) {
      // Usar requisitos configurados por el club
      requirements = clubClassConfig.requirements.map((req) => ({
        requirementId: req.id,
        name: req.name,
        completed: false,
      }));
    } else if (
      selectedClass.requirements &&
      selectedClass.requirements.length > 0
    ) {
      // Fallback: usar requisitos del catálogo si existen
      requirements = selectedClass.requirements.map((req) => ({
        requirementId: req.id,
        name: req.description,
        completed: false,
      }));
    }

    // Obtener nombre del miembro
    const memberName =
      selectedMember.person.fullName ||
      `${selectedMember.person.firstName || ""} ${selectedMember.person.lastName || ""}`.trim();

    const progressData = {
      personId: data.personId,
      memberId: data.memberId,
      memberName,
      clubId,
      periodId,
      classId: data.classId,
      className: selectedClass.name,
      clubType: club?.type as any,
      status: "in_progress" as const,
      startDate: Timestamp.now(),
      requirements,
      completionPercentage: 0,
    };

    try {
      await createMutation.mutateAsync(progressData);
      reset();
      setSelectedMember(null);
      setSelectedClass(null);
      onClose();
    } catch (error) {
      console.error("Error creating class progress:", error);
    }
  };

  if (!isOpen) return null;

  // Vista de progreso existente
  if (isViewing && classProgress) {
    return (
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto shadow-2xl animate-in fade-in zoom-in-95 duration-200">
          <div className="sticky top-0 bg-gradient-to-r from-gray-50 to-white border-b border-gray-100 px-6 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              {/* Imagen del pin de la clase */}
              <img
                src={getClassImage(classProgress.className)}
                alt={classProgress.className}
                className="w-14 h-14 rounded-xl object-contain bg-gray-50 shadow-md ring-2 ring-white"
                onError={(e) => {
                  (e.target as HTMLImageElement).src =
                    "/images/class/amigo.jpg";
                }}
              />
              <div>
                <h2 className="text-xl font-bold text-gray-900">
                  {classProgress.className}
                </h2>
                <Badge className={statusColors[classProgress.status]}>
                  {statusLabels[classProgress.status]}
                </Badge>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-xl transition-colors duration-200"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          <div className="p-6 space-y-6">
            {/* Mensaje de estado si está rechazada */}
            {classProgress.status === "rejected" &&
              classProgress.rejectionReason && (
                <div className="bg-gradient-to-r from-red-50 to-rose-50 border-2 border-red-200 rounded-xl p-4">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-red-400 to-red-600 rounded-xl flex items-center justify-center flex-shrink-0 shadow-md shadow-red-500/25">
                      <XCircle className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="font-semibold text-red-800">
                        Clase Rechazada
                      </p>
                      <p className="text-sm text-red-700 mt-1">
                        {classProgress.rejectionReason}
                      </p>
                    </div>
                  </div>
                </div>
              )}

            {/* Mensaje si está pendiente de aprobación */}
            {classProgress.status === "pending_approval" && (
              <div className="bg-gradient-to-r from-orange-50 to-amber-50 border-2 border-orange-200 rounded-xl p-4">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-orange-400 to-amber-500 rounded-xl flex items-center justify-center flex-shrink-0 shadow-md shadow-orange-500/25">
                    <Clock className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="font-semibold text-orange-800">
                      Pendiente de Aprobación
                    </p>
                    <p className="text-sm text-orange-700 mt-1">
                      Esta clase ha sido enviada a la Asociación para su
                      validación y aprobación.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Mensaje si está aprobada */}
            {classProgress.status === "approved" && (
              <div className="bg-gradient-to-r from-emerald-50 to-green-50 border-2 border-emerald-200 rounded-xl p-4">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-emerald-400 to-green-600 rounded-xl flex items-center justify-center flex-shrink-0 shadow-md shadow-emerald-500/25">
                    <Award className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="font-semibold text-emerald-800">
                      Clase Aprobada
                    </p>
                    <p className="text-sm text-emerald-700 mt-1">
                      La Asociación ha aprobado esta clase. Ya puede registrar
                      la investidura.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Progreso */}
            <div>
              <div className="flex justify-between text-sm mb-2">
                <span className="font-medium text-gray-700">
                  Progreso General
                </span>
                <span className="font-bold text-primary-600">
                  {classProgress.completionPercentage}%
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div
                  className="bg-primary-600 h-3 rounded-full transition-all duration-300"
                  style={{ width: `${classProgress.completionPercentage}%` }}
                />
              </div>
            </div>

            {/* Requisitos */}
            <div>
              <h3 className="font-semibold text-gray-900 mb-3">Requisitos</h3>
              <div className="space-y-2">
                {classProgress.requirements?.map((req) => (
                  <button
                    key={req.requirementId}
                    onClick={() =>
                      handleToggleRequirement(req.requirementId, req.completed)
                    }
                    disabled={
                      classProgress.status === "invested" ||
                      classProgress.status === "pending_approval" ||
                      classProgress.status === "approved" ||
                      updateRequirementMutation.isPending
                    }
                    className={`w-full flex items-center gap-3 p-3 rounded-xl border-2 transition-all duration-300 ${
                      req.completed
                        ? "bg-gradient-to-r from-green-50 to-emerald-50 border-green-200"
                        : "bg-gray-50 border-gray-100 hover:bg-gray-100 hover:border-gray-200"
                    } ${
                      classProgress.status === "invested" ||
                      classProgress.status === "pending_approval" ||
                      classProgress.status === "approved"
                        ? "cursor-not-allowed opacity-60"
                        : ""
                    }`}
                  >
                    {req.completed ? (
                      <div className="w-6 h-6 bg-gradient-to-br from-green-400 to-emerald-500 rounded-full flex items-center justify-center shadow-sm">
                        <CheckCircle className="w-4 h-4 text-white" />
                      </div>
                    ) : (
                      <Circle className="w-6 h-6 text-gray-300 flex-shrink-0" />
                    )}
                    <span
                      className={`text-left text-sm ${
                        req.completed
                          ? "text-green-800 font-medium"
                          : "text-gray-700"
                      }`}
                    >
                      {req.name}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            {/* Acciones */}
            <div className="flex flex-col gap-3 pt-4 border-t border-gray-100">
              {/* Botón: Marcar como Completada (solo si está en progreso y al 100%) */}
              {classProgress.status === "in_progress" &&
                classProgress.completionPercentage === 100 && (
                  <Button
                    onClick={handleCompleteClass}
                    disabled={completeClassMutation.isPending}
                    className="w-full bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 shadow-lg shadow-green-500/25 transition-all duration-300"
                  >
                    {completeClassMutation.isPending ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                      <CheckCircle className="w-4 h-4 mr-2" />
                    )}
                    Marcar como Completada
                  </Button>
                )}

              {/* Botón: Enviar a Aprobación (solo si está completada) */}
              {classProgress.status === "completed" && (
                <Button
                  onClick={handleSubmitForApproval}
                  disabled={submitForApprovalMutation.isPending}
                  className="w-full bg-gradient-to-r from-orange-500 to-amber-600 hover:from-orange-600 hover:to-amber-700 shadow-lg shadow-orange-500/25 transition-all duration-300"
                >
                  {submitForApprovalMutation.isPending ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <Send className="w-4 h-4 mr-2" />
                  )}
                  Enviar a Aprobación de la Asociación
                </Button>
              )}

              {/* Botón: Registrar Investidura (solo si está aprobada) */}
              {classProgress.status === "approved" && (
                <Button
                  onClick={handleInvestClass}
                  disabled={investClassMutation.isPending}
                  className="w-full bg-gradient-to-r from-yellow-500 to-amber-500 hover:from-yellow-600 hover:to-amber-600 shadow-lg shadow-yellow-500/25 transition-all duration-300"
                >
                  {investClassMutation.isPending ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <GraduationCap className="w-4 h-4 mr-2" />
                  )}
                  Registrar Investidura
                </Button>
              )}

              {/* Si fue rechazada, puede volver a enviar después de corregir */}
              {classProgress.status === "rejected" &&
                classProgress.completionPercentage === 100 && (
                  <Button
                    onClick={handleSubmitForApproval}
                    disabled={submitForApprovalMutation.isPending}
                    className="w-full bg-gradient-to-r from-orange-500 to-amber-600 hover:from-orange-600 hover:to-amber-700 shadow-lg shadow-orange-500/25 transition-all duration-300"
                  >
                    {submitForApprovalMutation.isPending ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                      <Send className="w-4 h-4 mr-2" />
                    )}
                    Reenviar a Aprobación
                  </Button>
                )}

              <Button
                variant="secondary"
                onClick={onClose}
                className="shadow-sm"
              >
                Cerrar
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Vista de crear nuevo progreso
  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto shadow-2xl animate-in fade-in zoom-in-95 duration-200">
        <div className="sticky top-0 bg-gradient-to-r from-gray-50 to-white border-b border-gray-100 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-green-400 to-emerald-600 rounded-xl flex items-center justify-center shadow-md shadow-green-500/25">
              <GraduationCap className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">
                Iniciar Clase Progresiva
              </h2>
              <p className="text-sm font-medium text-gray-500">
                Asignar clase a miembro
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-xl transition-colors duration-200"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-6">
          {/* Selección de Miembro */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Miembro *
            </label>
            {selectedMember ? (
              <div className="flex items-center justify-between bg-gradient-to-r from-gray-50 to-white rounded-xl p-3 border border-gray-100">
                <div className="flex items-center gap-3">
                  {selectedMember.person.photoUrl ? (
                    <img
                      src={selectedMember.person.photoUrl}
                      alt={selectedMember.person.fullName}
                      className="w-10 h-10 rounded-xl object-cover shadow-sm"
                    />
                  ) : (
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary-400 to-primary-600 flex items-center justify-center text-white shadow-sm">
                      <User className="w-5 h-5" />
                    </div>
                  )}
                  <span className="font-medium">
                    {selectedMember.person.fullName}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setSelectedMember(null);
                    setValue("personId", "");
                    setValue("memberId", "");
                  }}
                  className="text-gray-400 hover:text-gray-600 p-1.5 hover:bg-gray-100 rounded-xl transition-colors duration-200"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <div className="space-y-2">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    placeholder="Buscar miembro por nombre..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
                {loadingMembers ? (
                  <div className="flex items-center gap-2 text-gray-500 py-2">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span className="text-sm">Cargando miembros...</span>
                  </div>
                ) : filteredMembers.length > 0 ? (
                  <div className="max-h-40 overflow-y-auto border-2 border-gray-100 rounded-xl divide-y">
                    {filteredMembers.slice(0, 10).map((member) => (
                      <button
                        key={member.id}
                        type="button"
                        onClick={() => handleSelectMember(member)}
                        className="w-full text-left px-3 py-2 hover:bg-gray-50 flex items-center gap-3 transition-colors duration-200"
                      >
                        <User className="w-4 h-4 text-gray-400" />
                        <span className="text-sm">
                          {member.person.fullName}
                        </span>
                      </button>
                    ))}
                  </div>
                ) : searchTerm ? (
                  <p className="text-sm text-gray-500 py-2">
                    No se encontraron miembros
                  </p>
                ) : null}
              </div>
            )}
            {errors.personId && (
              <p className="text-red-500 text-xs mt-1">
                {errors.personId.message}
              </p>
            )}
          </div>

          {/* Selección de Clase */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Clase Progresiva *
            </label>
            {loadingClasses ? (
              <div className="flex items-center gap-2 text-gray-500">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span className="text-sm">Cargando clases...</span>
              </div>
            ) : (
              <div className="space-y-2">
                <Select {...register("classId")} error={!!errors.classId}>
                  <option value="">Seleccionar clase...</option>
                  {filteredClasses.map((cls) => (
                    <option key={cls.id} value={cls.id}>
                      {cls.name}
                      {cls.isAdvanced ? " ⭐" : ""}
                      {cls.gmLevel
                        ? ` (${cls.gmLevel === "guia_mayor" ? "GM" : cls.gmLevel === "gm_avanzado" ? "GMA" : "GMI"})`
                        : ""}
                    </option>
                  ))}
                </Select>
                <p className="text-xs text-gray-500">⭐ = Nivel Avanzado</p>
              </div>
            )}
            {errors.classId && (
              <p className="text-red-500 text-xs mt-1">
                {errors.classId.message}
              </p>
            )}
          </div>

          {/* Info de clase seleccionada */}
          {selectedClass && (
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-4 border-2 border-green-100">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-8 h-8 bg-gradient-to-br from-green-400 to-emerald-500 rounded-xl flex items-center justify-center shadow-sm shadow-green-500/25">
                  <GraduationCap className="w-4 h-4 text-white" />
                </div>
                <span className="font-semibold text-green-900">
                  {selectedClass.name}
                </span>
                {selectedClass.isAdvanced && (
                  <Badge className="bg-yellow-100 text-yellow-800 text-xs">
                    Avanzado
                  </Badge>
                )}
                {selectedClass.gmLevel && (
                  <Badge className="bg-purple-100 text-purple-800 text-xs">
                    {selectedClass.gmLevel === "guia_mayor"
                      ? "Guía Mayor"
                      : selectedClass.gmLevel === "gm_avanzado"
                        ? "GM Avanzado"
                        : "GM Instructor"}
                  </Badge>
                )}
              </div>
              {selectedClass.description && (
                <p className="text-sm text-green-700 mb-2">
                  {selectedClass.description}
                </p>
              )}
              {selectedClass.minAge && (
                <p className="text-xs text-green-600 mb-2">
                  Edad recomendada: {selectedClass.minAge}
                  {selectedClass.maxAge
                    ? ` - ${selectedClass.maxAge}`
                    : "+"}{" "}
                  años
                </p>
              )}

              {/* Mostrar requisitos del club o advertencia */}
              {loadingConfig ? (
                <div className="flex items-center gap-2 text-gray-500 mt-3">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span className="text-sm">Cargando requisitos...</span>
                </div>
              ) : clubClassConfig?.requirements &&
                clubClassConfig.requirements.length > 0 ? (
                <div className="mt-3">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm font-medium text-green-800">
                      Requisitos configurados (
                      {clubClassConfig.requirements.length}):
                    </p>
                    <button
                      type="button"
                      onClick={() => setShowConfigModal(true)}
                      className="text-xs text-green-600 hover:text-green-800 flex items-center gap-1 transition-colors duration-200"
                    >
                      <Settings className="w-3 h-3" />
                      Editar
                    </button>
                  </div>
                  <ul className="space-y-1">
                    {clubClassConfig.requirements.slice(0, 5).map((req) => (
                      <li
                        key={req.id}
                        className="text-sm text-green-700 flex items-start gap-2"
                      >
                        <Circle className="w-3 h-3 mt-1 flex-shrink-0" />
                        {req.name}
                      </li>
                    ))}
                    {clubClassConfig.requirements.length > 5 && (
                      <li className="text-sm text-green-600 italic">
                        ...y {clubClassConfig.requirements.length - 5}{" "}
                        requisitos más
                      </li>
                    )}
                  </ul>
                </div>
              ) : (
                <div className="mt-3 bg-gradient-to-r from-yellow-50 to-amber-50 border-2 border-yellow-200 rounded-xl p-3">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 text-yellow-600 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-yellow-800">
                        Sin requisitos configurados
                      </p>
                      <p className="text-xs text-yellow-700 mt-1">
                        Configura los requisitos que deben cumplir los miembros
                        para esta clase.
                      </p>
                      <button
                        type="button"
                        onClick={() => setShowConfigModal(true)}
                        className="mt-2 text-sm font-medium text-yellow-800 hover:text-yellow-900 flex items-center gap-1 transition-colors duration-200"
                      >
                        <Settings className="w-4 h-4" />
                        Configurar Requisitos
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Botones */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <Button type="button" variant="secondary" onClick={onClose}>
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={
                isSubmitting ||
                createMutation.isPending ||
                !selectedMember ||
                (!clubClassConfig?.requirements?.length &&
                  !selectedClass?.requirements?.length)
              }
            >
              {isSubmitting || createMutation.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  Iniciando...
                </>
              ) : (
                <>
                  <GraduationCap className="w-4 h-4 mr-2" />
                  Iniciar Clase
                </>
              )}
            </Button>
          </div>
        </form>

        {/* Modal de configuración de requisitos */}
        {showConfigModal && selectedClass && (
          <ClassRequirementConfigModal
            isOpen={showConfigModal}
            onClose={() => setShowConfigModal(false)}
            clubId={clubId}
            classId={selectedClass.id}
            className={selectedClass.name}
          />
        )}
      </div>
    </div>
  );
}
